import {
    differenceInCalendarDays,
    format,
    isSameMonth,
    isValid,
    startOfDay,
    startOfMonth,
    subMonths,
} from 'date-fns';
import { useCallback, useState } from 'react';

import type { CalendarDateRangePresetKey, DateRangeKey } from 'types';

const API_DATE_FORMAT = 'yyyy-MM-dd';
const MAX_RANGE_DAYS = 366;

const MONTHS_BY_PRESET: Record<CalendarDateRangePresetKey, number> = {
    thisMonth: 1,
    '3Months': 3,
    '6Months': 6,
    '12Months': 12,
};

const getPresetRange = (key: CalendarDateRangePresetKey, today = new Date()) => ({
    from: startOfMonth(subMonths(today, MONTHS_BY_PRESET[key] - 1)),
    to: today,
});

export const BILLING_REVENUE_PERIOD_PRESETS: CalendarDateRangePresetKey[] = [
    'thisMonth',
    '3Months',
    '6Months',
    '12Months',
];

export function useBillingRevenuePeriod() {
    const initialRange = getPresetRange('thisMonth');
    const [rangeKey, setRangeKeyState] =
        useState<DateRangeKey<CalendarDateRangePresetKey>>('thisMonth');
    const [fromDate, setFromDate] = useState<Date | null>(initialRange.from);
    const [toDate, setToDate] = useState<Date | null>(initialRange.to);

    const setRangeKey = useCallback((key: DateRangeKey<CalendarDateRangePresetKey>) => {
        setRangeKeyState(key);
        if (key === 'custom') {
            return;
        }
        const range = getPresetRange(key);
        setFromDate(range.from);
        setToDate(range.to);
    }, []);

    const hasValidDateRange = Boolean(
        fromDate &&
        toDate &&
        isValid(fromDate) &&
        isValid(toDate) &&
        startOfDay(fromDate) <= startOfDay(toDate) &&
        differenceInCalendarDays(startOfDay(toDate), startOfDay(fromDate)) < MAX_RANGE_DAYS
    );

    return {
        rangeKey,
        setRangeKey,
        fromDate,
        setFromDate,
        toDate,
        setToDate,
        hasValidDateRange,
        from: hasValidDateRange && fromDate ? format(fromDate, API_DATE_FORMAT) : undefined,
        to: hasValidDateRange && toDate ? format(toDate, API_DATE_FORMAT) : undefined,
        granularity:
            fromDate && toDate && !isSameMonth(fromDate, toDate)
                ? ('month' as const)
                : ('day' as const),
    };
}
